import type { ProjectBrief, Task } from '@dyspatch/core';
import { Section, SectionHead, Surface } from '@/components/ui/section';
import { JobGraph } from '@/features/graph/JobGraph';
import { isSyntheticKey } from '@/lib/briefs';

/**
 * The job's graph, as a region of the job's page.
 *
 * This was a page of its own, at `/jobs/:id/graph`, and the reason it was one is
 * the reason this whole change exists: the job lived in a 400px rail and a DAG
 * does not fit in 400px, so the graph got a route and a `Draw the graph` link,
 * and following it threw away the context you were reading the plan in. The job
 * is a page now, so the graph is a region on it, sized to the page.
 *
 * Both retired paths are redirects into that page (see `router.tsx`): the plain
 * one lands on `GRAPH_ANCHOR` and the one carrying a step opens that step there.
 * Nobody's saved link breaks.
 *
 * **The planning task is not on the graph.** Its product *was* the graph, so
 * drawing it would put a rootless node beside the work on every job an agent
 * planned. The plan list beside it still carries it, which is where a failed
 * planning run has to be readable.
 *
 * **A plan with no edges is not drawn.** Eight independent tasks laid out by
 * `layoutGraph` are one column of eight cards: 240px wide, eleven hundred tall,
 * inside a box the width of the page, saying nothing the plan list directly
 * above did not already say with more detail per row. That was most of the
 * height of a job page and all of its empty space. A picture of a DAG earns its
 * place when there is a dependency in it to see; when there is not, the honest
 * drawing is the sentence that says so.
 */
export const GRAPH_ANCHOR = 'graph';

export function JobGraphSection({
  brief,
  tasks,
  added,
  findings,
  selectedKey,
  onSelect,
  needsPlan,
}: {
  brief: ProjectBrief;
  /** Every step of the job; the rows the hub filed for itself are filtered out here. */
  tasks: readonly Task[];
  /** Keys an amendment added that nobody has approved yet; drawn with a ring. */
  added: ReadonlySet<string>;
  /** Per task key, how many agent findings nobody has filed as an issue. */
  findings: ReadonlyMap<string, number>;
  selectedKey: string | null;
  onSelect: (taskId: string) => void;
  needsPlan: boolean;
}) {
  const drawn = tasks.filter((task) => !isSyntheticKey(task.key));
  /**
   * Whether there is a dependency between two steps that are both on this
   * graph. `dependsOn` naming a key the list does not carry is dropped by
   * `layoutGraph` as half an edge, so the same rule decides it here: what
   * matters is whether the picture would contain a single line.
   */
  const keys = new Set(drawn.map((task) => task.key));
  const hasEdge = drawn.some((task) =>
    task.dependsOn.some((key) => key !== task.key && keys.has(key)),
  );

  if (drawn.length > 0 && !hasEdge) {
    return (
      <Section id={GRAPH_ANCHOR}>
        <SectionHead title="The graph" />
        <Surface>
          <p className="text-fg-3 px-gutter py-3 text-sm">
            {drawn.length === 1
              ? 'One task, waiting on nothing. There is no shape to draw.'
              : `All ${drawn.length} tasks are independent: none waits on another, so they can run in any order and there is no shape to draw. The plan above is the whole picture.`}
          </p>
        </Surface>
      </Section>
    );
  }

  return (
    <Section id={GRAPH_ANCHOR}>
      <SectionHead
        title="The graph"
        // The clause stands down on a phone: under `sm` the graph is the flat
        // list, and a heading whose meta leaves four characters for the title is
        // worse than a heading with none.
        meta={
          drawn.length ? (
            <span className="max-sm:hidden">
              a task sits one column past everything it waits on
            </span>
          ) : undefined
        }
      />
      <Surface>
        <JobGraph
          tasks={drawn}
          added={added}
          findings={findings}
          selectedKey={selectedKey}
          onSelect={(task) => {
            if (task.id) onSelect(task.id);
          }}
          empty={
            needsPlan
              ? `No tasks yet. Request a plan above and a worker drafts the graph for ${brief.repos[0] ?? 'this job'} here.`
              : 'No tasks to draw yet.'
          }
        />
      </Surface>
    </Section>
  );
}
